import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  TextField as MuiTextField,
} from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { useDelete, useNotify, useUpdate } from 'react-admin';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { BookingStatus } from '../../@generated/schemas';
import CloseIcon from '@mui/icons-material/Close';
import { Link } from 'react-router-dom';
import { LoadingElement } from '../../App';
import dayjs from 'dayjs';
import enLocale from 'date-fns/locale/en-GB';
import styled from 'styled-components';
import { useCreateNewBookingMutation } from '../../@generated/hooks';
import { useTranslate } from '../../locales';

export const LoadingElementWrapper = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  right: 0;
  top: 0;
  z-index: 1000;
  background: rgba(255, 255, 255, 0.5);
`;

const FormRow = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
`;

export const BookingSchedulerDialog = ({
  bookingId,
  startDate,
  endDate,
  bookingRequestId,
  description,
  vehicles,
  users,
  subtitle,
  bookingStatus,
  vehicleId,
  effectiveStartDate,
  effectiveEndDate,
  pickupLocationNotes,
  returnLocationNotes,
  adminBookingComment,
  userId,
  loading,
  setLoading,
  refetchBookings,
  setSelectedBookingParam,
  resetSelectedBooking,
}) => {
  const translate = useTranslate();
  const [createNewBooking] = useCreateNewBookingMutation();
  const [update] = useUpdate();
  const [deleteBookingRequest] = useDelete();
  const notify = useNotify();

  const handleCloseDetail = () => {
    if (!loading) {
      resetSelectedBooking(null);
    }
  };

  const handleSaveBooking = async () => {
    setLoading(true);

    if (bookingId && bookingId !== 'NEW') {
      await update(
        'Booking',
        {
          id: bookingId as string,
          data: {
            startDate: dayjs(startDate).format(),
            endDate: dayjs(endDate).format(),
            user: userId,
            vehicle: vehicleId,
            adminBookingComment,
          },
        },
        {
          onSuccess: async () => {
            setLoading(false);

            notify(translate('admin.bookingWasSuccessfullyUpdated'), {
              type: 'success',
            });
            await refetchBookings(true, {
              startDate,
              endDate,
            });
            handleCloseDetail();
          },
        },
      );
    } else {
      try {
        await createNewBooking({
          variables: {
            startDate: dayjs(startDate).format(),
            endDate: dayjs(endDate).format(),
            userId: userId,
            vehicleId,
            bookingComment: adminBookingComment || '',
            pickupLocationNotes: pickupLocationNotes || '',
            returnLocationNotes: returnLocationNotes || '',
          },
        });

        if (bookingRequestId) {
          await deleteBookingRequest('BookingRequest', {
            id: bookingRequestId,
          });
        }

        setLoading(false);

        notify(translate('admin.bookingWasSuccessfullyCreated'), {
          type: 'success',
        });
        await refetchBookings(true, {
          startDate,
          endDate,
        });
        handleCloseDetail();
      } catch (error) {
        setLoading(false);
        notify(error.message, { type: 'error' });
      }
    }
  };

  const handleCancelBooking = async () => {
    setLoading(true);

    await update(
      'Booking',
      {
        id: bookingId as string,
        data: {
          cancelDate: dayjs().format(),
          cancellationFees: 0,
        },
      },
      {
        onSuccess: async () => {
          setLoading(false);

          notify(translate('admin.bookingWasSuccessfullyCancelled'), {
            type: 'success',
          });
          await refetchBookings(true, {
            startDate,
            endDate,
          });
          handleCloseDetail();
        },
      },
    );
  };

  return (
    <Dialog
      open={!!bookingId}
      onClose={handleCloseDetail}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description">
      <DialogTitle>
        <div>
          {translate('admin.bookingColumn')}: {description}
        </div>
        {subtitle ? <div>Convadis: {subtitle}</div> : null}
        <div>
          {translate('admin.status')}:{' '}
          {bookingId && bookingId !== 'NEW' ? bookingStatus : 'Creating'}
        </div>
        <IconButton
          style={{ position: 'absolute', right: 18, top: 14 }}
          onClick={handleCloseDetail}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent id="modal-modal-title">
        {loading ? (
          <LoadingElementWrapper>
            <LoadingElement />
          </LoadingElementWrapper>
        ) : null}
        <DialogContentText style={{ paddingTop: 6 }}></DialogContentText>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <FormRow style={{ marginBottom: 20 }}>
            <DateTimePicker
              format="DD.MM.YYYY HH:mm"
              ampm={false}
              label={translate('admin.startDateTime')}
              value={dayjs(startDate)}
              onChange={(newValue) => {
                setSelectedBookingParam('startDate', newValue);
              }}
            />
            <DateTimePicker
              format="DD.MM.YYYY HH:mm"
              ampm={false}
              label={translate('admin.endDateTime')}
              value={dayjs(endDate)}
              onChange={(newValue) => {
                setSelectedBookingParam('endDate', newValue);
              }}
            />
          </FormRow>
          <FormRow style={{ marginBottom: 20 }}>
            <DateTimePicker
              disabled
              format="DD.MM.YYYY HH:mm"
              ampm={false}
              slotProps={{
                textField: {
                  error: false,
                },
              }}
              disableIgnoringDatePartForTimeValidation
              label={translate('admin.effectiveStartDateTime')}
              value={dayjs(effectiveStartDate)}
              onChange={() => undefined}
            />
            <DateTimePicker
              disabled
              format="DD.MM.YYYY HH:mm"
              ampm={false}
              slotProps={{
                textField: {
                  error: false,
                },
              }}
              label={translate('admin.effectiveEndDateTime')}
              value={dayjs(effectiveEndDate)}
              onChange={() => undefined}
            />
          </FormRow>
        </LocalizationProvider>
        <FormControl fullWidth style={{ marginBottom: 20 }}>
          <Autocomplete
            value={users.find((user) => user.id === userId)}
            size="small"
            getOptionLabel={(option) => option.label}
            options={users}
            onChange={(event: any, value: any) => {
              console.log('onChange', value);
              setSelectedBookingParam('userId', value.id);
            }}
            renderInput={(params) => (
              <MuiTextField
                {...params}
                placeholder=""
                label={translate('admin.user')}
              />
            )}
          />
        </FormControl>
        <FormControl fullWidth style={{ marginBottom: 20 }}>
          <Autocomplete
            value={vehicles.find((vehicle) => vehicle.id === vehicleId)}
            size="small"
            getOptionLabel={(option) =>
              `${option.plateNumber} (${option.name || ''} ${
                option.brandType.name
              } ${option.modelType.name})`
            }
            options={vehicles}
            onChange={(event: any, value: any) => {
              setSelectedBookingParam('vehicleId', value.id);
            }}
            renderInput={(params) => (
              <MuiTextField
                {...params}
                placeholder=""
                label={translate('admin.vehicle')}
              />
            )}
          />
        </FormControl>
        <FormControl fullWidth style={{ marginBottom: 20 }}>
          <MuiTextField
            value={adminBookingComment}
            size="small"
            onChange={(event) =>
              setSelectedBookingParam('adminBookingComment', event.target.value)
            }
            placeholder=""
            label="Comment"
          />
        </FormControl>
        <FormControl fullWidth style={{ marginBottom: 20 }}>
          <MuiTextField
            value={pickupLocationNotes}
            size="small"
            onChange={(event) =>
              setSelectedBookingParam('pickupLocationNotes', event.target.value)
            }
            placeholder=""
            label={translate('admin.pickupLocationNotes')}
          />
        </FormControl>
        <FormControl fullWidth style={{ marginBottom: 20 }}>
          <MuiTextField
            value={returnLocationNotes}
            size="small"
            onChange={(event) =>
              setSelectedBookingParam('returnLocationNotes', event.target.value)
            }
            placeholder=""
            label={translate('admin.returnLocationNotes')}
          />
        </FormControl>
      </DialogContent>
      <DialogActions style={{ justifyContent: 'flex-start' }}>
        <Button onClick={handleCloseDetail}>Close</Button>
        {bookingId && bookingId !== 'NEW' ? (
          <Link target="_blank" to={`/Booking/${bookingId}/show`}>
            <Button>{translate('admin.bookingDetail')}</Button>
          </Link>
        ) : null}

        {[
          undefined,
          BookingStatus.Late,
          BookingStatus.Confirmed,
          BookingStatus.NoShow,
          BookingStatus.OwnBooking,
          BookingStatus.Pending,
          BookingStatus.Running,
          BookingStatus.WaitingForUser,
        ].includes(bookingStatus) ? (
          <Button
            variant="contained"
            onClick={handleSaveBooking}
            disabled={dayjs(endDate).isBefore(startDate)}
            style={{ marginLeft: 'auto' }}>
            {translate('admin.save')}
          </Button>
        ) : null}
        {![
          BookingStatus.Aborted,
          BookingStatus.Cancelled,
          BookingStatus.PaymentFailed,
          BookingStatus.Revoked,
        ].includes(bookingStatus) &&
        bookingId &&
        bookingId !== 'NEW' ? (
          <Button
            variant="contained"
            color="error"
            onClick={handleCancelBooking}>
            {translate('admin.cancel')}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
};
